import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { BaseLayerData } from '@/lib/kinema';
import { useEditor, useEditorState, UPDATE_PROPERTY } from '@/lib/kinema';

export function ProjectSettingsModal({ onClose }: { onClose: () => void }) {
  const state = useEditorState();
  const root = state.layers[state.rootLayerId];
  if (!root) return null;

  // The form is a separate component so that its useState initialisers only run
  // once a root layer exists. Holding them here would put hooks after the early
  // return above, which breaks hook ordering across renders.
  return <ProjectSettingsForm root={root} rootLayerId={state.rootLayerId} onClose={onClose} />;
}

function ProjectSettingsForm({
  root,
  rootLayerId,
  onClose,
}: {
  root: BaseLayerData;
  rootLayerId: string;
  onClose: () => void;
}) {
  const editor = useEditor();
  const [name, setName] = useState(root.name);
  const [width, setWidth] = useState(root.width);
  const [height, setHeight] = useState(root.height);
  const [duration, setDuration] = useState(root.duration as number);
  const [frameRate, setFrameRate] = useState(root.frameRate as number);

  const save = () => {
    editor.batch(() => {
      if (name !== root.name) editor.dispatchCommand(UPDATE_PROPERTY, { layerId: rootLayerId, property: 'name', value: name });
      if (width !== root.width) editor.dispatchCommand(UPDATE_PROPERTY, { layerId: rootLayerId, property: 'width', value: width });
      if (height !== root.height) editor.dispatchCommand(UPDATE_PROPERTY, { layerId: rootLayerId, property: 'height', value: height });
      if (duration !== root.duration) editor.dispatchCommand(UPDATE_PROPERTY, { layerId: rootLayerId, property: 'duration', value: duration });
      if (frameRate !== root.frameRate) editor.dispatchCommand(UPDATE_PROPERTY, { layerId: rootLayerId, property: 'frameRate', value: frameRate });
    });
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.3)',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: 24,
          width: 380,
          color: '#1e293b',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Project Settings</h3>

        <div style={{ marginBottom: 12 }}>
          <label style={settingsLabelStyle}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} style={settingsInputStyle} />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={settingsLabelStyle}>Width</label>
            <input type="number" value={width} onChange={(e) => setWidth(parseInt(e.target.value) || 0)} style={settingsInputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={settingsLabelStyle}>Height</label>
            <input type="number" value={height} onChange={(e) => setHeight(parseInt(e.target.value) || 0)} style={settingsInputStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={settingsLabelStyle}>Duration (seconds)</label>
            <input type="number" value={duration} onChange={(e) => setDuration(parseFloat(e.target.value) || 0)} style={settingsInputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={settingsLabelStyle}>Frame Rate</label>
            <input type="number" value={frameRate} onChange={(e) => setFrameRate(parseInt(e.target.value) || 24)} style={settingsInputStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={settingsCancelBtnStyle}>Cancel</button>
          <button onClick={save} style={settingsSaveBtnStyle}>Save</button>
        </div>
      </div>
    </div>
  );
}

const settingsLabelStyle: CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  display: 'block',
  marginBottom: 4,
  fontWeight: 500,
};

const settingsInputStyle: CSSProperties = {
  width: '100%',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  color: '#1e293b',
  padding: '6px 10px',
  fontSize: 13,
  outline: 'none',
};

const settingsCancelBtnStyle: CSSProperties = {
  background: '#f1f5f9',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  color: '#475569',
  padding: '6px 16px',
  fontSize: 12,
  cursor: 'pointer',
};

const settingsSaveBtnStyle: CSSProperties = {
  background: '#2563eb',
  border: 'none',
  borderRadius: 6,
  color: '#fff',
  padding: '6px 16px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
